import { logger } from "@/utils/logger";

import { useState, useCallback, useRef } from 'react';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { sessionManager } from '@/utils/sessionManager';
// Cookie is no longer written on widget load; it's set on first user interaction in useChat.ts
import { useStateManager } from './useStateManager';

export interface ChatMessage {
  sender: 'User' | 'Dealer';
  timestamp: string;
  content: string;
}

export interface WebhookPayload {
  chatId: string;
  dealerId: string;
  name?: string;
  phone?: string;
  email?: string;
  source?: string;
  campaign?: string;
  conversation: ChatMessage[];
}

// Move utility functions outside the hook to avoid hoisting issues
const generateChatId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export const useChatSession = () => {
  const { sessionData } = useDealerSession();
  const { updateSessionData } = useStateManager();
  
  // Initialize session on hook creation - use centralized manager
  const [session] = useState(() => {
    const initializedSession = sessionManager.initializeSession();

    // Cookie is intentionally NOT set here — deferred until first user interaction
    // (see useChat.ts). This avoids marking non-engaged visitors with a session cookie.

    // Sync session data to state manager for persistence
    updateSessionData({
      sessionId: initializedSession.sessionId,
      chatId: initializedSession.chatId,
      dealerId: sessionData?.dealerId ? sessionData.dealerId.toString() : '0'
    });

    return initializedSession;
  });

  const dealerId = sessionData?.dealerId ? sessionData.dealerId.toString() : '0';
  const conversationRef = useRef<ChatMessage[]>([]);
  const contactDataRef = useRef<{ name?: string; phone?: string; email?: string }>({});

  const addMessage = useCallback((content: string, sender: 'User' | 'Dealer') => {
    const message: ChatMessage = {
      sender,
      timestamp: new Date().toISOString(),
      content
    };
    conversationRef.current.push(message);
    
    // Log to session storage asynchronously
    const sessionSender = sender === 'User' ? 'user' : 'assistant';
    sessionManager.logMessage(content, sessionSender).catch(error => {
      console.error('Failed to log message to session:', error);
    });
  }, []);

  const updateContactData = useCallback((data: { name?: string; phone?: string; email?: string }) => {
    contactDataRef.current = { ...contactDataRef.current, ...data };
    sessionManager.updateContactData(data);
  }, []);

  const sendWebhook = useCallback(async (eventType: 'chat_closed' | 'form_submission' | 'dealer_message') => {
    // DISABLE form_submission webhook to prevent duplicate lead pushes
    // Lead pushing is handled in the single point in useChatInterface.ts
    if (eventType === 'form_submission') {
      logger.log('Skipping form_submission webhook - handled by single point lead push system');
      return;
    }

    const payload: WebhookPayload = {
      chatId: session.chatId,
      dealerId,
      name: contactDataRef.current.name,
      phone: contactDataRef.current.phone,
      email: contactDataRef.current.email,
      source: "Website", // Default source
      campaign: "Chat", // Default campaign
      conversation: [...conversationRef.current]
    };


    const webhookUrl = 'https://admin.d2cmedia.ca/hooks/hook.chatbotboost.php';

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event: eventType,
          ...payload
        })
      });

      if (!response.ok) {
        throw new Error(`Webhook failed with status: ${response.status}`);
      }
      
      logger.log(`Webhook sent successfully for ${eventType}`);
    } catch (error) {
      // If CORS prevents us from reading the response, try with no-cors as fallback
      if (error instanceof TypeError && error.message.includes('CORS')) {
        try {
          await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            body: JSON.stringify({
              event: eventType,
              ...payload
            })
          });
          logger.log(`Webhook sent with no-cors fallback for ${eventType}`);
        } catch (fallbackError) {
          console.error('Webhook error (both normal and no-cors):', fallbackError);
        }
      } else {
        console.error('Webhook error:', error);
      }
    }
  }, [session.chatId, dealerId]);

  return {
    chatId: session.chatId,
    dealerId,
    sessionId: session.sessionId,
    addMessage,
    updateContactData,
    sendWebhook,
    getConversation: () => [...conversationRef.current],
    getContactData: () => ({ ...contactDataRef.current }),
    getCurrentSession: () => sessionManager.getCurrentSession()
  };
};
